import { dataService } from '../../data';
import type { deviceService, WSConnection } from '@unpispas/upp-base';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PosDeviceType } from '../types';
import { RasppiType } from '../types';

import { Rasppi } from './rasppi';

const _schema = {
    "name": "RASPPICONNECT",
    "fields": [
        { name: "status", type: "string", default: "AC" },
        { name: "rasppi", type: "objid" },
        { name: "posdevice", alias: "device", type: "objid" },
        { name: "connect", type: "string" },
        { name: "hassvc", type: "boolean", default: "1" },
    ],
    relate: [
        { direction: ">", target: "RASPPI", by: "connects", name: "rasppi", reason: "rasppi", child: false, class: RasppiType },
        { direction: ">", target: "POSDEVICE", by: "posdevice", name: "device", reason: "device", child: false, class: PosDeviceType },
    ]
} as const;

const _RasppiConnectClass = CreateObject(_schema);
if (!_RasppiConnectClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class RasppiConnect extends _RasppiConnectClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): RasppiConnect {
        return this._Copy(store) as RasppiConnect;
    }

    get IsValid(): boolean {
        return this.status == 'AC';
    }

    /**
     * Parsed local service endpoint from `connect` (`host:port`).
     * Single source for local URI validation; empty or malformed → null.
     */
    get EndPoint(): { host: string; port: number } | null {
        const _uri = String(this.connect || '').trim();
        if (!_uri){
            return null;
        }

        const _parts = _uri.split(':');
        if (_parts.length != 2){
            return null;
        }

        const _host = _parts[0].trim();
        const _port = Number(_parts[1].trim());
        if (!_host || !Number.isInteger(_port) || (_port < 1) || (_port > 65535)){
            return null;
        }

        return { host: _host, port: _port };
    }

    /** Whether `connect` is a parseable local service URI (`host:port`). */
    get IsLocal(): boolean {
        return this.EndPoint != null;
    }

    /**
     * Single path to open a WebSocket to this connect's service. Local endpoint
     * (`EndPoint`) takes priority; falls back to a remote gateway connection by
     * `rasppi.id` unless `opts.local` requires a local endpoint. Callers (feature
     * WSOpen wrappers, `wsstoreService`) do not rebuild `devices.Connect(...)`
     * args themselves.
     * @param devices Device service used to open the socket.
     * @param opts When `local` is true, rejects with null instead of opening a
     *   remote gateway.
     * @returns Open connection, or null when no devices, malformed URI, or
     *   local required but unavailable.
     */
    async Open(devices: deviceService, opts?: { local?: boolean }): Promise<WSConnection | null> {
        if (!devices){
            return null;
        }

        const _name = String(this.rasppi?.id || '').trim() || 'local';
        const _endpoint = this.EndPoint;

        if (_endpoint){
            return devices.Connect(_endpoint.host, _endpoint.port, _name, true);
        }

        if (String(this.connect || '').trim()){
            return null;
        }

        if (opts?.local){
            return null;
        }

        return devices.Connect(null, null, _name, false);
    }

    /**
     * Closes a connection previously opened via `Open`. Instance method to
     * mirror `Open`'s call shape (`connect.Open(...)` / `connect.Close(...)`);
     * does not read instance state, so it is safe to call with any connection
     * or with null.
     * @param connection Connection to close, or null.
     */
    Close(connection: WSConnection | null): void {
        connection?.Disconnect();
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (relation !== 'connects' || !(related instanceof Rasppi)){
            return;
        }

        if (related === this.rasppi){
            related.AddConnect(this);
        }
        else {
            related.DelConnect(this);
        }
    }
}
